import React, { useEffect } from "react";
import { FiCheck, FiMinus, FiShoppingCart, FiX } from "react-icons/fi";
import { toNumber } from "../../../utils/pos";

const sortOptions = (options) =>
  [...(Array.isArray(options) ? options : [])].sort(
    (first, second) => toNumber(first.sortOrder) - toNumber(second.sortOrder)
  );

const formatPrice = (price) => `${toNumber(price).toFixed(2)} TL`;

const SectionHeader = ({ eyebrow, title, description }) => (
  <div className="meal-options-section-header">
    <span>{eyebrow}</span>
    <div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  </div>
);

const SelectionMark = ({ selected, remove }) => (
  <span
    className={`meal-option-check ${selected ? "is-visible" : ""} ${
      remove ? "is-remove" : ""
    }`}
  >
    {remove ? <FiMinus /> : <FiCheck />}
  </span>
);

const MealOptionsMenu = ({
  item,
  checkedOptions,
  removedOptions,
  portion,
  priceRef,
  onCheckedOptionsChange,
  onRemovedOptionsChange,
  onPortionChange,
  onCustomPriceChange,
  onConfirm,
  onClose,
  getOptionPrice = (option) => option?.price,
}) => {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  if (!item) return null;

  const toggleOption = (option) => {
    onCheckedOptionsChange(
      checkedOptions.includes(option)
        ? checkedOptions.filter((candidate) => candidate !== option)
        : [...checkedOptions, option]
    );
  };

  const toggleRemovedOption = (option) => {
    onRemovedOptionsChange(
      removedOptions.includes(option)
        ? removedOptions.filter((candidate) => candidate !== option)
        : [...removedOptions, option]
    );
  };

  const extras = sortOptions(item.addOptions);
  const removals = sortOptions(item.removeOptions);
  const portions = sortOptions(item.portions);
  const selectedCount =
    checkedOptions.length + removedOptions.length + (portion ? 1 : 0);
  const totalPrice =
    toNumber(getOptionPrice(item)) +
    checkedOptions.reduce(
      (total, option) => total + toNumber(getOptionPrice(option)),
      0
    ) +
    toNumber(portion ? getOptionPrice(portion) : 0);

  return (
    <div className="meal-options-menu" role="presentation" onMouseDown={onClose}>
      <section
        className="meal-options-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="meal-options-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="meal-options-header">
          <div className="meal-options-product">
            <span className="meal-options-eyebrow">Özelleştir</span>
            <h2 id="meal-options-title">{item.name}</h2>
            {!item.showInMenu && (
              <span className="meal-options-base-price">
                Başlangıç {formatPrice(getOptionPrice(item))}
              </span>
            )}
          </div>
          <button
            type="button"
            className="meal-options-close"
            onClick={onClose}
            aria-label="Seçenekleri kapat"
          >
            <FiX />
          </button>
        </header>

        {item.showInMenu ? (
          <div className="meal-options-custom-price">
            <div className="meal-options-price-icon">₺</div>
            <div>
              <span>Ürün tutarı</span>
              <p>Bu ürün için uygulanacak satış fiyatını girin.</p>
            </div>
            <div className="meal-options-price-input">
              <input
                type="number"
                min="0"
                step="0.01"
                ref={priceRef}
                placeholder="0,00"
                onChange={(event) => onCustomPriceChange(event.target.value)}
                autoFocus
              />
              <span>TL</span>
            </div>
          </div>
        ) : (
          <div className="meal-options-body">
            {portions.length > 0 && (
              <section className="meal-options-section">
                <SectionHeader
                  eyebrow="1"
                  title="Porsiyon seçimi"
                  description="Ürün boyutunu seçin. Yalnızca bir porsiyon seçilebilir."
                />
                <div className="meal-options-grid meal-options-grid-portions">
                  <button
                    type="button"
                    className={`meal-option-card ${!portion ? "is-selected" : ""}`}
                    aria-pressed={!portion}
                    onClick={() => onPortionChange(null)}
                  >
                    <SelectionMark selected={!portion} />
                    <span className="meal-option-name">Standart</span>
                    <span className="meal-option-price">Fiyata dahil</span>
                  </button>
                  {portions.map((itemPortion) => {
                    const selected = portion === itemPortion;
                    return (
                      <button
                        key={itemPortion.id || itemPortion.name}
                        type="button"
                        className={`meal-option-card ${
                          selected ? "is-selected" : ""
                        }`}
                        aria-pressed={selected}
                        onClick={() => onPortionChange(itemPortion)}
                      >
                        <SelectionMark selected={selected} />
                        <span className="meal-option-name">{itemPortion.name}</span>
                        <span className="meal-option-price">
                          +{formatPrice(getOptionPrice(itemPortion))}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {extras.length > 0 && (
              <section className="meal-options-section">
                <SectionHeader
                  eyebrow={portions.length > 0 ? "2" : "1"}
                  title="Ekstralar"
                  description="Eklemek istediğiniz ürünleri seçin. Birden fazla seçim yapabilirsiniz."
                />
                <div className="meal-options-grid">
                  {extras.map((option) => {
                    const selected = checkedOptions.includes(option);
                    return (
                      <button
                        key={option.id || option.name}
                        type="button"
                        className={`meal-option-card ${
                          selected ? "is-selected" : ""
                        }`}
                        aria-pressed={selected}
                        onClick={() => toggleOption(option)}
                      >
                        <SelectionMark selected={selected} />
                        <span className="meal-option-name">{option.name}</span>
                        <span className="meal-option-price">
                          {toNumber(getOptionPrice(option)) > 0
                            ? `+${formatPrice(getOptionPrice(option))}`
                            : "Ücretsiz"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {removals.length > 0 && (
              <section className="meal-options-section meal-options-section-remove">
                <SectionHeader
                  eyebrow={
                    (portions.length > 0 ? 1 : 0) +
                    (extras.length > 0 ? 1 : 0) +
                    1
                  }
                  title="Çıkarılacaklar"
                  description="Üründe olmasını istemediğiniz malzemeleri işaretleyin."
                />
                <div className="meal-options-grid">
                  {removals.map((option) => {
                    const selected = removedOptions.includes(option);
                    return (
                      <button
                        key={option.id || option.name}
                        type="button"
                        className={`meal-option-card meal-option-card-remove ${
                          selected ? "is-selected" : ""
                        }`}
                        aria-pressed={selected}
                        onClick={() => toggleRemovedOption(option)}
                      >
                        <SelectionMark selected={selected} remove />
                        <span className="meal-option-name">{option.name}</span>
                        <span className="meal-option-price">
                          {selected ? "Çıkarılacak" : "Üründe kalsın"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}

        <footer className="meal-options-footer">
          {!item.showInMenu && (
            <div className="meal-options-summary">
              <span>
                {selectedCount > 0
                  ? `${selectedCount} seçim yapıldı`
                  : "Henüz seçim yapılmadı"}
              </span>
              <strong>{formatPrice(totalPrice)}</strong>
            </div>
          )}
          <div className="meal-options-actions">
            <button
              type="button"
              className="meal-options-cancel-button"
              onClick={onClose}
            >
              Vazgeç
            </button>
            <button
              type="button"
              className="meal-options-confirm-button"
              onClick={onConfirm}
            >
              <FiShoppingCart />
              Sepete ekle
              {!item.showInMenu && <span>{formatPrice(totalPrice)}</span>}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default MealOptionsMenu;
